/**
 * Which résumé fields the LMS's authoritative scorer can actually SEE.
 *
 * ⚠️ THIS EXISTS BECAUSE THE PANEL WAS OVER-PROMISING, AND THE EVIDENCE IS WORTH KEEPING.
 * A student accepted a summary rewrite and two experience rewrites for a Car Mechanic JD; the panel
 * projected ~38 and the LMS returned **25 — exactly the baseline**. `roleDomainFit` and
 * `skillOverlap` both came back 0. The rewrites were good. The scorer never saw them.
 *
 * `jobMatcher.service.js buildLlmStudentInputs` sends the model only:
 *
 *   resumeSummary   ← resumeData.summary
 *   resumeSkills    ← resumeData.skills[].name
 *   resumeExperience ← `${title} @ ${company}` for up to 4 roles
 *
 * `experience[].description` is NEVER sent. Neither is project text, certifications or education
 * prose. So a rewritten experience bullet cannot move the score by any amount — not a little, zero —
 * however much better it reads.
 *
 * Meanwhile `buildEditableResumeFields` offers each section item's `summary`-or-`description`, which
 * is precisely that invisible text. Most suggestions therefore landed on fields with no scoring
 * effect while the plan attributed points to them.
 *
 * WHAT THIS MODULE DOES, AND DOES NOT DO. It does not remove those suggestions — rewriting an
 * experience bullet toward the JD genuinely helps the human who reads the résumé, which is the point
 * of the exercise. It removes their *attributed gain*, so the projection only counts what the scorer
 * will actually reward, and labels them so the student knows which is which.
 *
 * THE OTHER HALF OF THE FIX IS NOT HERE. Making the scorer read experience descriptions would be
 * the real answer, and it changes `roleDomainFit`/`skillOverlap` for every student against every job
 * — invalidating every stored `JobStudentScores` row. That needs a re-score migration and sign-off,
 * so it is deliberately out of scope. Until it happens, this module is what keeps the number honest.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

/** Whether a change can move the authoritative match score at all. */
export type ScoreVisibility = "scored" | "unscored";

/**
 * The résumé summary. Sent verbatim (truncated to 800 chars) as `resumeSummary` and visible to the
 * scorer, though — unlike `skillOverlap` — it no longer moves `roleDomainFit`: that component is
 * judged purely from the candidate's stated dream roles and target industries against the job's
 * domain, never from résumé text.
 */
const SUMMARY_FIELD_PATH = "sections.summary.content";

/**
 * A skill's NAME — `sections.skills.items[N].name`, added by buildCoachEditableFields.
 *
 * Sent as `resumeSkills`, so retitling a skill to the job's own wording is scoreable. A skill's
 * `description` ("Database", "Methodology") is not sent and is not scoreable.
 */
const SKILL_NAME_PATH = /^sections\.skills\.items\[\d+]\.name$/;

/**
 * Can the scorer see the text at this fieldPath?
 *
 * Deliberately an ALLOW-LIST. A deny-list would silently start crediting any new field the builder
 * begins offering, which is the exact failure this module was written to fix — and it would fail in
 * the direction that over-promises to the student.
 */
export const scoreVisibilityOf = (fieldPath: string): ScoreVisibility => {
  const path = String(fieldPath ?? "").trim();

  if (path === SUMMARY_FIELD_PATH) return "scored";
  if (SKILL_NAME_PATH.test(path)) return "scored";

  return "unscored";
};

export const isScoredFieldPath = (fieldPath: string) => scoreVisibilityOf(fieldPath) === "scored";

/**
 * Drop the attributed gains of suggestions the scorer cannot see.
 *
 * Returns a new gains map rather than mutating, and leaves ids that are not field rewrites — skill
 * ADDITIONS — untouched: appending to the skills list changes `resumeSkills`, which is sent, so those
 * gains are real. They are also the strongest lever available, at weight 30.
 *
 * `fieldPathById` carries only field-rewrite suggestions. An id absent from it is therefore not a
 * rewrite and keeps its gain.
 */
export const dropUnscoredGains = <T extends Record<string, unknown>>({
  gains,
  fieldPathById,
}: {
  gains: T;
  fieldPathById: Record<string, string>;
}): T => {
  const kept = {} as Record<string, unknown>;

  for (const [id, gain] of Object.entries(gains)) {
    const fieldPath = fieldPathById[id];

    if (fieldPath !== undefined && !isScoredFieldPath(fieldPath)) continue;

    kept[id] = gain;
  }

  return kept as T;
};

/**
 * Explanation shown on a suggestion the score cannot reward, in place of a "+N" badge.
 *
 * Says what the change IS worth rather than only what it is not. "No score change" alone reads as
 * "pointless", and a student who concludes that will skip rewrites that materially improve how a
 * recruiter reads their résumé.
 */
export const UNSCORED_SUGGESTION_NOTE =
  "Improves how this reads for recruiters. Doesn't affect the match score.";
